import { ComponentType, useMemo } from "react";
import type { ColumnsType } from "antd/es/table";

import KpiCardJs from "../../../shared/Dashboard/KpiCard/KpiCard";
import DataTable from "../../../shared/dashboards/DataTable/DataTable";
import type {
  ExportRow,
  PaymentReportingResponse,
  ProviderRow,
} from "../../../../queries/payments/usePaymentReporting";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { KPI_CARD_BACKGROUND } from "../../../../utils/Constants";
import { formatCurrency as formatCurrencyBase } from "../../../../utils/financialFormatters";

import SectionHeader from "../SectionHeader/SectionHeader";

import styles from "./DepositsSection.module.css";

// KpiCard is JS — cast for TS callers.
const KpiCard = KpiCardJs as ComponentType<{
  title: string;
  value: number;
  percentage?: number;
  rate?: boolean;
  isApiRunning?: boolean;
  maximumFractionDigits?: number;
  backgroundColor?: string;
  hidePercentage?: boolean;
  currencySymbol?: string;
}>;

type Deposits = PaymentReportingResponse["deposits"];

interface DepositsSectionProps {
  deposits?: Deposits;
  isLoading?: boolean;
  exportRows?: ExportRow[];
  currencySymbol?: string;
}

const formatCount = (value: number): string =>
  Number(value || 0).toLocaleString("en-US");

const formatRate = (value: number): string => `${Number(value || 0)}%`;

export const getDepositsColumns = (
  currencySymbol?: string,
): ColumnsType<ProviderRow> => [
  {
    title: AppStrings.PROVIDER,
    dataIndex: "provider",
    key: "provider",
    sorter: (a, b) => String(a.provider).localeCompare(String(b.provider)),
  },
  {
    title: AppStrings.DEPOSITS,
    dataIndex: "deposits",
    key: "deposits",
    sorter: (a, b) => Number(a.deposits) - Number(b.deposits),
    render: (value: number) => formatCount(value),
  },
  {
    title: AppStrings.DEPOSIT_AMOUNT,
    dataIndex: "amount",
    key: "amount",
    sorter: (a, b) => Number(a.amount) - Number(b.amount),
    render: (value: number) =>
      formatCurrencyBase(Number(value || 0), currencySymbol, 2),
  },
  {
    title: AppStrings.ACCEPTANCE_RATE,
    dataIndex: "acceptance_rate",
    key: "acceptance_rate",
    sorter: (a, b) => Number(a.acceptance_rate) - Number(b.acceptance_rate),
    render: (value: number) => formatRate(value),
  },
];

const DepositsSection: React.FC<DepositsSectionProps> = ({
  deposits,
  isLoading = false,
  exportRows = [],
  currencySymbol,
}) => {
  const kpis = deposits?.kpis;
  const providers = deposits?.providers ?? [];
  const columns = useMemo(
    () => getDepositsColumns(currencySymbol),
    [currencySymbol],
  );

  return (
    <section className={styles.section}>
      <SectionHeader
        title={AppStrings.DEPOSITS}
        exportFilename="payment-reporting-deposits.xlsx"
        exportRows={exportRows}
      />
      <div className={styles.kpis}>
        <KpiCard
          title={AppStrings.TOTAL_DEPOSIT_AMOUNT}
          value={kpis?.total_amount.value ?? 0}
          percentage={kpis?.total_amount.percent_change}
          isApiRunning={isLoading}
          backgroundColor={KPI_CARD_BACKGROUND.GREY_750}
          currencySymbol={currencySymbol}
        />
        <KpiCard
          title={AppStrings.TOTAL_NUMBER_OF_DEPOSITS}
          value={kpis?.total_count.value ?? 0}
          percentage={kpis?.total_count.percent_change}
          isApiRunning={isLoading}
          maximumFractionDigits={0}
          backgroundColor={KPI_CARD_BACKGROUND.GREY_750}
        />
        <KpiCard
          title={AppStrings.ACCEPTANCE_RATE}
          value={kpis?.acceptance_rate.value ?? 0}
          percentage={kpis?.acceptance_rate.percent_change}
          isApiRunning={isLoading}
          rate
          maximumFractionDigits={0}
          backgroundColor={KPI_CARD_BACKGROUND.GREY_750}
        />
      </div>
      <div className={styles.tableWrapper}>
        <DataTable<ProviderRow>
          rowKey="key"
          dataSource={providers}
          columns={columns}
          loading={isLoading}
          pagination={false}
        />
      </div>
    </section>
  );
};

export default DepositsSection;
